import {PROMPT_TYPE} from 'rendition-core';
import type {GPTMessage} from '.';

const CODE_TEMPLATE = `
{STEPS_TO_MODIFY_CODE}
{mandatory: backticks to initiate code preview}
\`\`\`tsx
{{IMPORTS}}

{{PROPS}}

{{REACT_COMPONENT}}

export default () => {{RENDER_CALL}}
\`\`\`
{SUMMARY_OF_CHANGES}
`;

const SCOPED_CODE_TEMPLATE = `
The code you respond with must be the requested modification of the sub-section of code provided, and the code should use the following template:

CODE PREVIEW
\`\`\`tsx
{{MODIFIED_CODE}}
\`\`\`
`;

const PACKAGE_JSON = `
{
    "dependencies": {
      "react": "^18.0.0",
      "react-dom": "^18.0.0",
      "react-scripts": "^4.0.0",
      "@headlessui/react": "^1",
      "@heroicons/react": "^1",
      "webfontloader": "1.6.28"
    },
    "devDependencies": {
      "@types/react": "^18.0.0",
      "@types/react-dom": "^18.0.0",
      "typescript": "^4.0.0"
    },
}`;

const RUBRIC = `
The code will be evaluated according to the following rubric:

0. No content / elements lost
    ◦ fail: Refactored code is missing some copy, assets, elements, or other content from the original code. If resulting code is missing any text copy or svg urls from the original code, this test fails.
    ◦ success: All original assets, copy, and elements are present. This means that every word or sentence in the original text copy and all svg urls are present in the resulting code.
1. Use of Imports
    ◦ fail: Uses imports not listed in the provided package.json
    ◦ success: Strictly uses only imports from the provided package.json
2. Code Quality
    ◦ fail: Poor quality, hard to understand, lacks comments, has poor structure or naming conventions
    ◦ success: High quality, easy to understand, well-commented, uses good naming conventions and structure
3. Code Completeness
    ◦ fail: Contains abbreviations or placeholders such as {/* the rest of the code here */} or // code omitted for brevity
    ◦ success: Completely free of abbreviations or placeholders, contains only executable code
4. Code Errors
    ◦ fail: Contains errors that prevent compilation
    ◦ success: Free of errors, compiles successfully
5. Code Template Adherence
    ◦ fail: Doesn't fit within the provided code template
    ◦ success: Perfectly fits within the provided code template
6. Appropriate Language and Libraries
    ◦ fail: Not written in TypeScript, not suitable for use with React or doesn't use Tailwind
    ◦ success: Correctly written in TypeScript, suitable for use with React, and uses Tailwind
7. Code Block Limit
    ◦ fail: Contains multiple code blocks
    ◦ success: Contains exactly one code block
8. default prop values
    ◦ fail: no default values provided for props
    ◦ success: contains default values for props (or component does not take any props)
`;

// from testing helped by few shot -- provide an example interaction

// eslint-disable-next-line
const CHOOSE_BEST = `
Among these three efforts to refactor the original code, which is the best according to the following rubric?

${RUBRIC}

Please respond with only the number 1, 2, or 3
`;
/*  
    for gpt call, pass in the following:
    messages: [
        system: ${CHOOSE_BEST},
        ...CHOOSE_BEST_EXAMPLE_INTERACTION,
        user: `
            original code: ${originalCode}
            option 1: ${refactor1}
            option 2: ${refactor1}
            option 3: ${refactor1}
        `
    ]
*/

// eslint-disable-next-line
const CHOOSE_BEST_EXAMPLE_INTERACTION = [
    {
        user: `original code: import React from "react";
        const App: React.FC = () => {
        return (
            <div className="flex flex-col justify-between gap-1 w-20 items-start px-1">
            <div className="text-right text-xs font-['Gilroy-Light'] leading-[12px] text-[#00cb6a] ml-3">
                Sale
            </div>
            <div className="self-end flex flex-row mr-2 gap-px w-6 items-center">
                <img
                src="https://file.rendit.io/n/fazd6o5lCHcmZbw4r3ns.png"
                className="w-2 shrink-0"
                id="Image1"
                />
                <div className="text-right text-xs font-['Gilroy-Regular'] leading-[14.4px] text-[#f5f8fa] self-start">
                140
                </div>
            </div>
            <div className="text-right text-xs font-['Gilroy-Light'] leading-[12px] text-[#aab8c2]">
                6 Minutes ago
            </div>
            </div>
        );
        };

        export default App;


        option 1:  import React from "react";

        const App: React.FC = () => {
        return (
            <div className="flex flex-col justify-between gap-1 w-20 items-start px-1">
            <div className="text-right text-xs font-['Gilroy-Light'] leading-[12px] text-[#00cb6a] ml-3">
                Sale
            </div>
            <div className="self-end flex flex-row mr-2 gap-px w-6 items-center">
                <img
                src="https://file.rendit.io/n/fazd6o5lCHcmZbw4r3ns.png"
                className="w-2 shrink-0"
                id="Image1"
                />
                <div className="text-right text-xs font-['Gilroy-Regular'] leading-[14.4px] text-[#f5f8fa] self-start">
                140
                </div>
            </div>
            <div className="text-right text-xs font-['Gilroy-Light'] leading-[12px] text-[#aab8c2]">
                6 Minutes ago
            </div>
            </div>
        );
        };

        export default App;

        option 2: <div>
        // rest of the content here
        </div>

        option 3: // importing necessary modules from react
        import React from "react";

        // defining properties for the component
        interface AppProps {
        saleLabel: string;
        imageSource: string;
        itemNumber: number | string;
        timeLabel: string;
        }

        const App: React.FC<AppProps> = ({
        saleLabel,
        imageSource,
        itemNumber,
        timeLabel,
        }) => {
        // returning the JSX with properties substituting the original hardcoded values
        return (
            <div className="flex flex-col justify-between gap-1 w-20 items-start px-1">
            <div className="text-right text-xs font-['Gilroy-Light'] leading-[12px] text-[#00cb6a] ml-3">
                {saleLabel}
            </div>
            <div className="self-end flex flex-row mr-2 gap-px w-6 items-center">
                <img src={imageSource} className="w-2 shrink-0" id="Image1" />
                <div className="text-right text-xs font-['Gilroy-Regular'] leading-[14.4px] text-[#f5f8fa] self-start">
                {itemNumber}
                </div>
            </div>
            <div className="text-right text-xs font-['Gilroy-Light'] leading-[12px] text-[#aab8c2]">
                {timeLabel}
            </div>
            </div>
        );
        };

        export default App;`,
        assistant: '3',
    },
];

// eslint-disable-next-line
const CHECK_AGAINST_RUBRIC = `
Please evaluate the refactored user code according to this rubric:

The code will be evaluated according to the following rubric:

${RUBRIC}

In your response, please respond like this:
Use of Imports: pass/fail
...
default prop values: pass/fail
OVERALL: (one of the following:)
PASS
PLACEHOLDER
CONTENT/PROPS
BUG
OTHER FAILURE

Respond with PASS if the code passes the rubric
Respond with PLACEHOLDER if the code contains placeholders or abbreviations
Respond with CONTENT/PROPS if the code is missing content or default prop values
Respond with BUG if the code contains errors
Respond with OTHER FAILURE if the code fails the rubric for any other reason
`;
/**
 * send along with the original user code and the refactored user code
 * 
    messages: [
        system: ${CHECK_AGAINST_RUBRIC},
        user: `
            original code: ${originalCode}
            refactored code: ${bestRefactor}
        `
    ]
*/

// eslint-disable-next-line
const RETRY_PLACEHOLDERS = `
Please rewrite the user code to remove all placeholders and abbreviations. Fill in placeholders and abbreviations with the correct code.

The user message will contain the original code, and the user code which contains placeholders or abbreviations
`;
/**
 * send along with the original user code and the refactored user code containing placeholder
 *     messages: [
        system: ${RETRY_PLACEHOLDERS},
        user: `
            original code: ${originalCode}
            refactored code containing placeholders: ${codeContainingPlaceholders}
        `
    ]
 */

// eslint-disable-next-line
const RETRY_CONTENT_LOSS = `
    Please rewrite the user code to include all content (images, text, elements) from the original code. Make sure to include default props for the component where appropriate.
    
    The user message will contain the original code, and the user code which is missing content from the original code
    `;
/**
     * send along with the original user code and the refactored user code containing placeholder
     *     messages: [
            system: ${RETRY_CONTENT_LOSS},
            user: `
                original code: ${originalCode}
                refactored code missing content: ${codeMissingContent}
            `
        ]
     */

// eslint-disable-next-line
const RETRY_BUG = `
Please rewrite the user code to fix the bug.
`;
/**
 * send along with the original user code and the refactored user code containing placeholder
 *     messages: [
        system: ${RETRY_BUG},
        user: `
            code: ${codeWithBug}
            issue: ${stackTraceFromSandbox}
        `
    ]
*/

// eslint-disable-next-line
const RETRY_OTHER_FAILURE = `
Please rewrite the user code to fix the issue. The code fails the following rubric: 
${RUBRIC}
please communicate why the code fails the rubric, the steps you will take to fix it, and then the fixed code.
`;
/**
 * send the original user code and the refactored user code
 *     messages: [
        system: ${RETRY_OTHER_FAILURE},
        user: `
            original code: ${originalCode}
            code which fails the rubric (please rewrite this code to pass the rubric): ${stackTraceFromSandbox}
        `
    ]
*/

// eslint-disable-next-line
export const PROMPTS_REFACTOR_JOB = `
Your role is to refactor the code to use props, and add code comments.

INSTRUCTIONS FOR UPDATING CODE
Please make sure that all of the text, assets, and other elements are present -- no content should be lost on refactor.

The code should use the following template:

${CODE_TEMPLATE}

Please do not import libraries other than those contained in the package.json file:
${PACKAGE_JSON}

${RUBRIC}

Your goal in writing code is to succeed based on the above criteria. Before writing code, please restate the 7 criteria above in your own words to ensure you understand them. If you have any questions, please ask them before writing code.
`;
/**
 * send this request three times, include original user code and the refactored user code
 *     messages: [
        system: ${PROMPTS_REFACTOR_JOB},
        user: `
            code to refactor: ${originalCode}
        `
    ]
*/

const SCOPED_CODE_TEMPLATE_GUIDELINES = `
Using the template given, please insert the new code in the place of the text {{MODIFIED_CODE}}.
You may ask clarifying questions or provide other context as you see fit.
Please do not import libraries other than those contained in the package.json file:
${PACKAGE_JSON}`;

const BASE_JOB = `
Your role to support the user in crafting exceptional functional React components using typescript and tailwind css.

You will receive a request from the user, and you may respond in one of three ways:

1. CHAT
If the user does not ask for a change to the code, please respond with text, and do not include any code blocks in your response.

2. CLARIFICATION
If the user asks for a change to the code and you are less than 90% confident that you can make the change, please ask the user for clarification, and do not include any code blocks in your response.

3. CODE
If the user asks for changes to the code and you are more than 90% confident you can execute on this task without error, please update the code. If the result would be more than 150 lines of code, please request that the user break the task down into smaller tasks.
`;

const UPDATE_JOB = `
${BASE_JOB}

INSTRUCTIONS FOR UPDATING CODE
Do not modify styles or content unless necessary to fulfill the user's request. 
Before updating the code, think through the problem step by step. 
First list the steps you will take to update the code, then update the code, and then summarize the changes you made.

The code should use the following template:

${CODE_TEMPLATE}

Please do not import libraries other than those contained in the package.json file:
${PACKAGE_JSON}

${RUBRIC}

Your goal in writing code is to succeed based on the above criteria. Before writing code, please restate the 7 criteria above in your own words to ensure you understand them. If you have any questions, please ask them before writing code.
`;

const SCOPED_JOB = `${BASE_JOB}
Please modify this component according to the user request and make sure it follows this template:
${SCOPED_CODE_TEMPLATE}
${SCOPED_CODE_TEMPLATE_GUIDELINES}
`;

export const getModifyCodePrompt = (
    previousMessages: GPTMessage[],
    promptType: PROMPT_TYPE,
    previousCode: string,
    modifications: string,
    scopedCode?: string
): GPTMessage[] => {
    const scopedMessage: GPTMessage = {
        role: 'user',
        content: `This is the existing code:
\`\`\`
${previousCode}
\`\`\`

Given the following sub-section of the code:
\`\`\`
${scopedCode}
\`\`\`

Please make the following modifications to the sub-section of the code per the users request:\n${modifications}
The modifications you make should be limited to the scope of the code provided and please do not add anything extra to the code.
Please return the modified sub-section of code following the template.`,
    };

    const unScopedMessage: GPTMessage = {
        role: 'user',
        content: `This is the existing code:
\`\`\`
${previousCode}
\`\`\`
The user's request is as follows:
${modifications}`,
    };

    const coreMessage = scopedCode ? scopedMessage : unScopedMessage;

    return [
        {
            role: 'system',
            content: scopedCode ? SCOPED_JOB : UPDATE_JOB,
        },
        ...previousMessages,
        coreMessage,
    ];
};
